import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Button, ErrorBanner } from '../components/ui.js';
import { errorMessage, trpcClient } from '../lib/trpc.js';
import { AuthFrame } from './login.js';

/**
 * Turning the dashboard's summary email off from the link in it, with no sign-in. The link
 * carries its token after `#`, so it never reaches a server log, and nothing changes until the
 * button is pressed: mail scanners open links on their own.
 */
export function TurnOffSummaryScreen() {
  const token = window.location.hash.replace(/^#/, '');
  const turnOff = useMutation({
    mutationFn: () => trpcClient.dashboard.turnOffSummaryEmail.mutate({ token }),
  });
  if (turnOff.isSuccess)
    return (
      <AuthFrame title={turnOff.data ? 'The summary email is off' : 'This link doesn’t work'}>
        <p className="text-sm text-slate-600" data-testid="summary-email-result">
          {turnOff.data
            ? `You won’t get the ${turnOff.data.company} summary by email any more. You can turn it back on in Settings.`
            : 'It may be incomplete, or you may no longer have an account here. Sign in and change it in Settings.'}
        </p>
        <Link to="/settings" className="mt-4 inline-block text-sm font-semibold text-brand-strong">
          Open Settings
        </Link>
      </AuthFrame>
    );
  return (
    <AuthFrame title="Turn off the summary email?">
      {!token && (
        <ErrorBanner>
          This link is incomplete. Open the link from the summary email again.
        </ErrorBanner>
      )}
      {turnOff.error && <ErrorBanner>{errorMessage(turnOff.error)}</ErrorBanner>}
      <p className="mb-4 text-sm text-slate-600">
        You’ll stop getting the dashboard’s morning summary by email. You can turn it back on in
        Settings at any time.
      </p>
      <Button
        variant="primary"
        className="w-full"
        busy={turnOff.isPending}
        disabled={!token}
        onClick={() => turnOff.mutate()}
      >
        Turn it off
      </Button>
    </AuthFrame>
  );
}
